import React, { useState, useEffect, useCallback } from 'react';
import { Box, Card, Typography, Button, IconButton, CircularProgress, Chip, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useMediaQuery, useTheme, List, ListItem, Divider, Switch, FormControlLabel,  } from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Store as StoreIcon,
  Close as CloseIcon,
  LockReset as ResetIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import client from '../../api/client';
import Pagination from '../../components/Pagination';
import CustomTextField from '../../components/CustomTextField';

const ShopsList = () => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const [formData, setFormData] = useState({
    shop_name: '',
    mobile: '',
    phone: '',
    address: '',
    is_subscribed: false,
    subscription_end_date: '',
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchShops = useCallback(async () => {
    setLoading(true);
    try {
      const params = { 
        page: currentPage, 
        page_size: pageSize,
      };
      if (search) params.search = search;
      
      const response = await client.get('/users/admin/shops/', { params });
      const data = response.data;
      
      setShops(data.results || []);
      setTotalCount(data.total_count || 0);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      toast.error('خطا در دریافت لیست');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, search]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on search
      fetchShops();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch on page/pageSize change
  useEffect(() => {
    fetchShops();
  }, [currentPage, pageSize]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // باز کردن فرم
  const handleOpenDialog = (shop = null) => {
    if (shop) {
      setSelectedShop(shop);
      setFormData({
        shop_name: shop.shop_name || '',
        mobile: shop.mobile || '',
        phone: shop.phone || '',
        address: shop.address || '',
        is_subscribed: shop.is_subscribed || false,
        subscription_end_date: shop.subscription_end_date || '',
      });
    } else {
      setSelectedShop(null);
      setFormData({
        shop_name: '',
        mobile: '',
        phone: '',
        address: '',
        is_subscribed: false,
        subscription_end_date: '',
      });
    }
    setDialogOpen(true);
  };

  // ذخیره
  const handleSubmit = async () => {
    if (!formData.shop_name.trim()) {
      toast.error('نام فروشگاه الزامی است');
      return;
    }
    if (!formData.mobile.trim()) {
      toast.error('شماره موبایل الزامی است');
      return;
    }
    if (formData.mobile.length !== 11 || !formData.mobile.startsWith('09')) {
      toast.error('شماره موبایل باید ۱۱ رقم و با 09 شروع شود');
      return;
    }

    setSaving(true);
    try {
      if (selectedShop) {
        await client.put(`/users/admin/shops/${selectedShop.id}/`, formData);
        toast.success('فروشگاه ویرایش شد');
      } else {
        await client.post('/users/admin/shops/create/', formData);
        toast.success('فروشگاه جدید اضافه شد');
      }
      setDialogOpen(false);
      fetchShops();
    } catch (error) {
      const msg = error.response?.data?.error || 'خطا در ذخیره';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // حذف
  const handleDelete = async () => {
    if (!selectedShop) return;
    
    setSaving(true);
    try {
      await client.delete(`/users/admin/shops/${selectedShop.id}/delete/`);
      toast.success('فروشگاه حذف شد');
      setDeleteDialogOpen(false);
      setSelectedShop(null);
      fetchShops();
    } catch (error) {
      toast.error('خطا در حذف');
    } finally {
      setSaving(false);
    }
  };

  // ریست پسورد
  const handleResetPassword = async (shop) => {
    if (!window.confirm(`رمز عبور ${shop.shop_name} به شماره موبایل ریست شود؟`)) return;
    
    try {
      await client.post(`/users/admin/shops/${shop.id}/reset-password/`);
      toast.success('رمز عبور ریست شد');
    } catch (error) {
      toast.error('خطا');
    }
  };

  // نمایش موبایل
  const MobileView = () => (
    <List disablePadding>
      {shops.map((shop, index) => (
        <React.Fragment key={shop.id}>
          {index > 0 && <Divider />}
          <ListItem sx={{ flexDirection: 'column', alignItems: 'stretch', py: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StoreIcon color="primary" />
                <Typography fontWeight="bold">{shop.shop_name}</Typography>
              </Box>
              <Chip
                label={shop.is_subscribed ? 'مشترک' : 'غیرمشترک'}
                size="small"
                color={shop.is_subscribed ? 'success' : 'default'}
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 1, color: 'text.secondary' }}>
              <Typography variant="body2">
                <PhoneIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                {shop.mobile}
              </Typography>
              {shop.phone && <Typography variant="body2">تلفن: {shop.phone}</Typography>}
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  مشتری: {shop.customers_count || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  سفارش: {shop.orders_count || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  فاکتور: {shop.invoices_count || 0}
                </Typography>
              </Box>
              <Box>
                <IconButton size="small" color="primary" onClick={() => handleOpenDialog(shop)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="warning" onClick={() => handleResetPassword(shop)}>
                  <ResetIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => { setSelectedShop(shop); setDeleteDialogOpen(true); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </ListItem>
        </React.Fragment>
      ))}
    </List>
  );

  // نمایش دسکتاپ
  const DesktopView = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>نام فروشگاه</TableCell>
            <TableCell>موبایل</TableCell>
            <TableCell>تلفن</TableCell>
            <TableCell>نوع اشتراک</TableCell>
            <TableCell>تعداد مشتری</TableCell>
            <TableCell>تعداد سفارش</TableCell>
            <TableCell>تعداد فاکتور</TableCell>
            <TableCell align="center">عملیات</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shops.map((shop) => (
            <TableRow key={shop.id} hover>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StoreIcon color="primary" fontSize="small" />
                  {shop.shop_name}
                </Box>
              </TableCell>
              <TableCell dir="ltr">{shop.mobile}</TableCell>
              <TableCell dir="ltr">{shop.phone || '—'}</TableCell>
              <TableCell>
                <Chip
                  label={shop.is_subscribed ? 'مشترک' : 'غیرمشترک'}
                  size="small"
                  color={shop.is_subscribed ? 'success' : 'default'}
                />
              </TableCell>
              <TableCell>{shop.customers_count || 0}</TableCell>
              <TableCell>{shop.orders_count || 0}</TableCell>
              <TableCell>{shop.invoices_count || 0}</TableCell>
              <TableCell align="center">
                <IconButton size="small" color="primary" onClick={() => handleOpenDialog(shop)}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="warning" onClick={() => handleResetPassword(shop)}>
                  <ResetIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => { setSelectedShop(shop); setDeleteDialogOpen(true); }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
        پرده فروشی‌ها
      </Typography>

      {/* هدر */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <CustomTextField           placeholder="جستجوی نام فروشگاه..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ minWidth: 140, height: { xs: 48, sm: 40 } }}
        >
          افزودن فروشگاه
        </Button>
      </Box>

      {/* محتوا */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : shops.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <StoreIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">
            {search ? 'فروشگاهی با این نام یافت نشد' : 'پرده فروشی یافت نشد'}
          </Typography>
        </Card>
      ) : (
        <>
          <Card component={Paper}>
            {isMobile ? <MobileView /> : <DesktopView />}
          </Card>
          
          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}

      {/* دیالوگ افزودن/ویرایش */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedShop ? 'ویرایش فروشگاه' : 'فروشگاه جدید'}
          {isMobile && (
            <IconButton onClick={() => setDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <CustomTextField                 label="نام فروشگاه *"
                value={formData.shop_name}
                onChange={(e) => setFormData({ ...formData, shop_name: e.target.value })}
                fullWidth
                autoFocus
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField                 label="شماره موبایل *"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                placeholder="09123456789"
                inputProps={{ maxLength: 11, dir: 'ltr' }}
                fullWidth
                helperText={!selectedShop ? 'رمز عبور اولیه = شماره موبایل' : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField                 label="تلفن ثابت"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                inputProps={{ dir: 'ltr' }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField                 label="آدرس"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_subscribed}
                    onChange={(e) => setFormData({ ...formData, is_subscribed: e.target.checked })}
                    color="success"
                  />
                }
                label="اشتراک فعال"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField                 label="تاریخ پایان اشتراک"
                type="date"
                value={formData.subscription_end_date}
                onChange={(e) => setFormData({ ...formData, subscription_end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={!formData.is_subscribed}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} fullWidth={isMobile}>
            انصراف
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving} fullWidth={isMobile}>
            {saving ? <CircularProgress size={24} /> : 'ذخیره'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* دیالوگ حذف */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>حذف فروشگاه</DialogTitle>
        <DialogContent>
          <Typography>آیا از حذف <strong>{selectedShop?.shop_name}</strong> مطمئن هستید؟</Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>این عملیات قابل بازگشت نیست!</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>انصراف</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'حذف'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShopsList;